import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { NAVIGATION_LINKS, CONTACT_INFO } from '../utils/constants';
import logo from '../logo.png';

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const toggleMenu = () => setIsMenuOpen((v) => !v);
  const closeMenu = () => setIsMenuOpen(false);
  const isActiveLink = (path) => location.pathname === path;

  return (
    <header
      className={`bg-white sticky top-0 z-50 border-b transition-all duration-200 ${
        isScrolled ? 'border-line shadow-header' : 'border-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex justify-between items-center h-16 sm:h-[72px]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 shrink-0" onClick={closeMenu}>
            <img src={logo} alt="ASHUD Logo" className="h-9 sm:h-10 w-auto" />
            <div className="hidden sm:block leading-tight">
              <span className="block text-ink font-bold text-base tracking-tight">
                ASHUD TCHAD
              </span>
              <span className="block text-muted text-xs font-medium">
                Solidarité · Performance · Essor
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center" aria-label="Navigation principale">
            <ul className="flex items-center gap-1">
              {NAVIGATION_LINKS.map(({ path, label }) => (
                <li key={path}>
                  <Link
                    to={path}
                    aria-current={isActiveLink(path) ? 'page' : undefined}
                    className={`relative px-4 py-2 text-sm font-medium rounded transition-colors duration-200 ${
                      isActiveLink(path)
                        ? 'text-primary'
                        : 'text-body hover:text-primary'
                    }`}
                  >
                    {label}
                    {isActiveLink(path) && (
                      <span className="absolute left-4 right-4 -bottom-[1px] h-0.5 bg-primary rounded-full" />
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href={`tel:${CONTACT_INFO.phone}`}
              className="flex items-center gap-2 text-sm font-medium text-body hover:text-primary transition-colors duration-200"
            >
              <Phone className="w-4 h-4" aria-hidden="true" />
              <span>(+235) 61 90 91 32</span>
            </a>
            <Link
              to="/devenir-membre"
              className="bg-primary hover:bg-primary-dark text-white text-sm font-semibold px-4 py-2 rounded transition-colors duration-200"
            >
              Devenir membre
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="lg:hidden p-2 -mr-2 text-ink hover:text-primary transition-colors duration-200 rounded"
            aria-label={isMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-nav"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <div
          id="mobile-nav"
          className={`lg:hidden overflow-hidden transition-all duration-200 ease-out ${
            isMenuOpen ? 'max-h-[26rem] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <nav className="py-4 border-t border-line" aria-label="Navigation mobile">
            <ul className="space-y-1">
              {NAVIGATION_LINKS.map(({ path, label }) => (
                <li key={path}>
                  <Link
                    to={path}
                    aria-current={isActiveLink(path) ? 'page' : undefined}
                    className={`block px-3 py-3 text-base font-medium rounded transition-colors duration-200 ${
                      isActiveLink(path)
                        ? 'text-primary bg-primary-light'
                        : 'text-body hover:bg-surface'
                    }`}
                    onClick={closeMenu}
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-4 pt-4 border-t border-line space-y-3">
              <a href={`tel:${CONTACT_INFO.phone}`} className="flex items-center gap-2 px-3 text-sm text-body">
                <Phone className="w-4 h-4 text-primary" aria-hidden="true" />
                (+235) 61 90 91 32
              </a>
              <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-center gap-2 px-3 text-sm text-body">
                <Mail className="w-4 h-4 text-primary" aria-hidden="true" />
                {CONTACT_INFO.email}
              </a>
              <div className="px-3 pt-1">
                <Link
                  to="/devenir-membre"
                  onClick={closeMenu}
                  className="block w-full text-center bg-primary hover:bg-primary-dark text-white text-sm font-semibold px-4 py-3 rounded transition-colors duration-200"
                >
                  Devenir membre
                </Link>
              </div>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
}

export default Header;
